import React, { useState, useRef } from "react";
import { DatasetDataInfo } from "models/dataset-models";
import useCopyDataset from "hooks/useCopyDataset";
import { Tooltip } from "react-tooltip";
import { formatDate } from "utils/time-utils";
import { formatDatasetValue } from "utils/value-format-utils";
import DatasetTooltipText, {
  MappedValue,
} from "components/basic/dataset-tooltip-text/DatasetTooltipText";
import useChartDialogStore from "store/chart-dialog-store";
import useDatasetIdentifiersStore from "store/dataset-identifiers-store";

import "./UniversalTableDatasetButtonComponent.scss";
import { CellType, DatasetButtonComponentProps } from "./UniversalDatasetTableTypes";

const DOUBLE_CLICK_THRESHOLD = 300;

const UniversalTableDatasetButtonComponent = ({
  id,
  columnIndex,
  rowData,
  dataset,
  data,
  time,
  compactMode = false,
  isExpired,
}: DatasetButtonComponentProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const clickTimeRef = useRef(Date.now());
  const { openDialog } = useChartDialogStore();
  const selectedIdsStore = useDatasetIdentifiersStore();
  const { ids: selectedIds } = selectedIdsStore;

  useCopyDataset(isHovered, dataset?.title, dataset?.identifier);

  const isSelected = dataset?.identifier && selectedIds.has(dataset.identifier);

  const onButtonClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!dataset?.identifier) return;

    const now = Date.now();
    const isDoubleClick = now - clickTimeRef.current < DOUBLE_CLICK_THRESHOLD;
    const ctrlPressed = e.ctrlKey;

    if (isDoubleClick) {
      if (!ctrlPressed) {
        selectedIdsStore.clear();
      }
      selectedIdsStore.addIdentifier(dataset.identifier);
      openDialog();
    } else if (ctrlPressed) {
      if (!selectedIds.has(dataset.identifier)) {
        selectedIdsStore.addIdentifier(dataset.identifier);
      } else {
        selectedIdsStore.removeIdentifier(dataset.identifier);
      }
    } else {
      selectedIdsStore.clear();
    }

    clickTimeRef.current = now;
  };

  const datasetInfo: DatasetDataInfo = {
    ...dataset,
    loadedTime: new Date(),
  };

  const currentData = isExpired ? undefined : data;

  return (
    <>
      {dataset && (
        <Tooltip anchorSelect={`#${id}`} style={{ zIndex: 99999 }}>
          <DatasetTooltipText data={currentData} time={time} dataset={datasetInfo} />
        </Tooltip>
      )}
      <div
        className={`button-clickable ${isSelected ? "selected-label" : ""} ${compactMode ? "universal-table-compact-button-wrapper" : "button-wrapper"}`}
        id={id ?? ""}
        onClick={onButtonClick}
        onPointerEnter={() => setIsHovered(true)}
        onPointerLeave={() => setIsHovered(false)}
      >
        <i>
          {dataset?.stringValueMapper &&
          !rowData.columns[columnIndex].cell.displayDatasetValueTime ? (
            <MappedValue mapValue={data?.value} mapperId={dataset.stringValueMapper} />
          ) : (
            <div>
              {rowData.columns[columnIndex].cell.type === CellType.DATASET &&
              rowData.columns[columnIndex].cell.displayDatasetValueTime ? (
                <div>
                  {formatDate(
                    data?.key,
                    rowData.columns[columnIndex].cell.timePattern ?? "HH:mm:ss"
                  )}
                </div>
              ) : (
                <>
                  {formatDatasetValue(datasetInfo, currentData)}
                  {dataset?.unit && !isExpired && ` ${dataset.unit}`}
                </>
              )}
            </div>
          )}
        </i>
      </div>
    </>
  );
};

export default UniversalTableDatasetButtonComponent;
